import aiStudioService from "@apis/aiStudioService";
import libraryService from "@apis/libraryService";
import { uuid13 } from "@utils/util";
import report from "@report";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useSearchParams } from "react-router-dom";
import message from "@utils/message";
import { fetchAiCredits } from "@store/globalSlice";

let insightCancelled = false;

export interface ApiStatus {
  status: Status;
  data: any;
  error?: any;
}

export const useVideoScript = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const videoId = searchParams.get("id");
  const videoSource = searchParams.get("videoSource") || "";
  const country = searchParams.get("country") || "";
  const collectionId = searchParams.get("collectionId") || "";
  const { userInfo } = useAppSelector((state) => state.globalReducer);
  const { totalRemain: remainCredit } = useAppSelector((state) => state.globalReducer.creditInfo);

  const [hasPurchased, setHasPurchased] = useState(false);
  const [productItem, setProductItem] = useState<ApiStatus>({ status: "idle", data: null, error: null });
  const [videoScript, setVideoScript] = useState<ApiStatus>({ status: "idle", data: null, error: null });
  const [collectionDetail, setCollectionDetail] = useState<ApiStatus>({ status: "idle", data: null, error: null });
  const [videoInsight, setVideoInsight] = useState<ApiStatus>({ status: "idle", data: null, error: null });
  const refTimer = useRef<ReturnType<typeof setInterval>>();
  // 解析轮询的状态
  const [status, setStatus] = useState<Status>("idle");
  const [translateStatus, setTranslateStatus] = useState<Status>("idle");
  const [error, setErr] = useState<any>(null);
  const [defaultParam, setDefaultParam] = useState<any>({ id: videoId });
  const [pageStatus, setPageStatus] = useState<Status>("loading");
  // const [pageStep, setPageStep] = useState<string>("idle");
  const dispatch = useAppDispatch();

  useEffect(() => {
    const data = {
      ...defaultParam,
      videoId,
      videoSource: videoSource,
      product_id: (productItem as any)?.id,
      product_title: (productItem as any)?.productTitle,
      category: [
        (productItem as any)?.priCateName,
        (productItem as any)?.secCateName,
        (productItem as any)?.terCateName,
      ]
        .filter((item) => item)
        .join(" > "),
    };
    setDefaultParam(data);
  }, [productItem]);

  const showTranslate = async (translate: boolean = false) => {
    if (translate) {
      try {
        setTranslateStatus("loading");
        await requestScriptAnlysisResult({ translate });
        setTranslateStatus("succeeded");
      } catch (error) {}
    } else {
      setVideoScript({ ...videoScript, translate: false } as any);
    }
  };

  // 根据视频id获取商品列表，获取第一个商品id
  const requestProductInfo = async () => {
    const res: any = await aiStudioService.fetchProductInfo({
      videoId,
      videoSource,
      country,
    });
    setProductItem(res);
  };

  // 根据视频收藏状态
  const requestVideoCollection = async () => {
    try {
      const res: any = await aiStudioService.fetchVideoCollection({
        id: videoId,
      });
      if (res.status) {
        setCollectionDetail((prev: any) => ({ ...prev, collectionId: res.collectionId, isCollect: res.status }));
      }
    } catch (error) {
      setCollectionDetail((prev: any) => ({ ...prev, isCollect: false }));
    }
  };

  const requestScriptAnlysisResult = async (collectionDetail?: any) => {
    const res: any = await aiStudioService.fetchScriptAnlysisResult({
      id: videoId,
      videoSource,
      translate: collectionDetail?.translate || false,
      collectionId,
      country,
    });
    setVideoScript({ ...res, translate: collectionDetail?.translate });
    setHasPurchased(true);
  };

  const startAITask = async (resolve: any, { requestId }: { requestId: string }) => {
    if (insightCancelled) {
      clearInterval(refTimer.current);
      resolve(false);
      return;
    }

    try {
      const data = await aiStudioService.startScriptAnlysisTask<{ code: number }>(
        {
          id: videoId,
          videoSource,
          requestId,
        },
        { country },
      );

      if (insightCancelled) {
        clearInterval(refTimer.current);
        resolve(false);
        return;
      }

      // 1 进行中，2 成功，3 超时，4 积分不足，5 参数不合法
      if (data.code === 2) {
        clearInterval(refTimer.current);
        try {
          await requestScriptAnlysisResult();
          setHasPurchased(true);
          resolve(true);
          setStatus("succeeded");
          report.event("video_script_result", {
            kalo_type: "script_export",
            kalo_value: "success",
          });
        } catch (err: any) {
          setHasPurchased(false);
          setErr(err);
          report.event("video_script_result", {
            kalo_type: "script_export",
            kalo_value: "failed",
            kalo_json: {
              reason: err?.message || err || "Unknown error",
            },
          });
          setStatus("failed");
          resolve(true);
        } finally {
          dispatch(fetchAiCredits());
        }
      } else if (data.code === 3) {
        clearInterval(refTimer.current);
        setHasPurchased(false);
        setErr({
          code: "1305",
          message:
            "Loading failed, your credits have been refunded. Please try to refresh the page. If the problem persists, please contact us.",
        });
        setStatus("failed");
        report.event("video_script_result", {
          kalo_type: "script_export",
          kalo_value: "failed",
          kalo_json: {
            reason: "task timeout",
          },
        });
        resolve(true);
      } else if (data.code === 4) {
        clearInterval(refTimer.current);
        setErr({
          code: "1306",
          message: "Not enough credits.",
        });
        setStatus("failed");
        report.event("video_script_result", {
          kalo_type: "script_export",
          kalo_value: "failed",
          kalo_json: {
            reason: "not enough credits",
          },
        });
      } else if (data.code === 5) {
        clearInterval(refTimer.current);
        setErr({
          code: "1307",
          message: "Sorry, You don't have permission to access this page.",
        });
        setStatus("failed");
        report.event("video_script_result", {
          kalo_type: "script_export",
          kalo_value: "failed",
          kalo_json: {
            reason: "Invalid parameter",
          },
        });
      } else if (data.code === 6) {
        clearInterval(refTimer.current);
        setErr({
          code: "1308",
          message: "Resource not ready.",
        });
        setStatus("failed");
        report.event("video_script_result", {
          kalo_type: "script_export",
          kalo_value: "failed",
          kalo_json: {
            reason: "resource not ready",
          },
        });
      } else if (data.code === 7) {
        clearInterval(refTimer.current);
        setErr({
          code: "1309",
          message: "Not support.",
        });
        setStatus("failed");
        report.event("video_script_result", {
          kalo_type: "script_export",
          kalo_value: "failed",
          kalo_json: {
            reason: "not support",
          },
        });
      }
    } catch (error) {
      setStatus("failed");
    }
  };

  const loopAITask = () => {
    insightCancelled = false;
    const requestId = uuid13();
    return new Promise((resolve) => {
      setStatus("loading");
      startAITask(resolve, { requestId });
      refTimer.current = setInterval(async () => {
        startAITask(resolve, { requestId });
      }, 5000);
    });
  };

  const requestCollectionDetail = async () => {
    try {
      setStatus("loading");
      const data: any = await libraryService.fetchCollectionDetail({
        collectionId,
      });
      setCollectionDetail({ ...data, collectionId });
      await requestScriptAnlysisResult(data);
      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
    }
  };

  const checkAiUse = async () => {
    setPageStatus("loading");

    const resUsed = await aiStudioService.fetchCheckAiUse({ id: videoId || "", type: "videoScript" });
    sessionStorage.setItem(`${userInfo?.username}_videoInsight_${videoId}`, JSON.stringify(resUsed));
    const isUsed = Boolean(resUsed?.used);
    setHasPurchased(isUsed);
    setPageStatus("succeeded");
    if (collectionId) {
      // 收藏反显
      requestCollectionDetail();
    } else {
      requestVideoCollection();
      if (isUsed || remainCredit > 0) {
        try {
          await loopAITask();
        } catch (err) {
          setStatus("failed");
        }
      }
    }
  };

  const handleVideoInsight = async (videoLink: string) => {
    setStatus("loading");
    setVideoInsight({ status: "loading", data: { videoLink } });

    try {
      const data: any = await aiStudioService.fetchVideoLinkPreview({
        videoLink,
      });
      // 需要等待 videoUrl 生成完成才算成功
      setVideoInsight({ status: "loading", data: { ...data, videoLink } });

      if (data?.videoId) {
        setSearchParams({
          id: data.videoId,
          videoSource: data.videoSource || "",
          country: data.country || "",
        });
      }

      return data;
    } catch (error: any) {
      setVideoInsight((prev) => ({ ...prev, status: "failed", error: error?.message }));
      message.error((error?.message as any) || "Video link not found, Please re-enter it.");
      setStatus("failed");
    }
  };

  const requestVideoURL = async (videoId: string | null, videoSource: string = "Kalodata") => {
    if (videoId) {
      setVideoInsight((prev) => ({ ...prev, status: "loading", error: null }));
      try {
        const data = await aiStudioService.fetchVideoURL({ id: videoId, videoSource });
        setVideoInsight((prev) => ({ data: { ...prev.data, url: data?.url }, status: "succeeded", error: null }));
      } catch (err: any) {
        setVideoInsight((prev: any) => ({ data: { ...prev.data, url: "" }, status: "failed", error: err as any }));
      }
    }
  };

  const reload = () => {
    if (videoInsight.status === "failed" && !videoInsight.data?.videoId) {
      // 链接解析错误
      handleVideoInsight(videoInsight.data.videoLink);
      setStatus("loading");
    } else if (videoInsight.status === "failed") {
      // 视频获取错误
      requestVideoURL(videoId, videoSource);
      setStatus("loading");
    } else if (status === "failed") {
      // 视频解析错误
      loopAITask();
    }
  };

  useEffect(() => {
    if (videoInsight.status === "failed") setStatus("failed");
    if (videoInsight.status !== "succeeded") return;

    checkAiUse();
    if (!collectionId) {
      requestProductInfo();
    }
    return () => {
      clearInterval(refTimer.current);
    };
  }, [videoInsight.status]);

  useEffect(() => {
    // 清理定时器
    clearInterval(refTimer.current);

    if (videoId) {
      requestVideoURL(videoId, videoSource);
    } else {
      // 重置所有状态
      // setStatus("idle");
      // setHasPurchased(false);
      // setErr(null);
      setVideoScript({ status: "idle", data: null, error: null });
      setCollectionDetail({ status: "idle", data: null, error: null });
      setVideoInsight({ status: "idle", data: null, error: null });
      setProductItem({ status: "idle", data: null, error: null });
      setDefaultParam({ id: videoId });
      // setPageStatus("idle");
    }
  }, [videoId, videoSource]);

  const stopPolling = useCallback(() => {
    insightCancelled = true;
    clearInterval(refTimer.current);
    refTimer.current = undefined;
  }, []);

  const resetAll = useCallback(() => {
    stopPolling();
    setSearchParams({});
    insightCancelled = false;
  }, []);

  return {
    refTimer,
    hasPurchased,
    videoScript,
    status,
    error,
    defaultParam,
    collectionDetail,
    pageStatus,
    videoId,
    videoSource,
    collectionId,
    videoInsight,
    translateStatus,
    reload,
    checkAiUse,
    loopAITask,
    showTranslate,
    setCollectionDetail,
    handleVideoInsight,
    stopPolling,
    resetAll,
  };
};
